export type CustomEmojiToken = {
  type: "custom_emoji";
  customEmojiId: string;
  fallback: string;
};

export type EmojiTextNode =
  | { type: "text"; value: string }
  | CustomEmojiToken;

export type EmojiReplacementTarget = {
  start: number;
  end: number;
  fallback: string;
};

const customEmojiPattern = /<tg-emoji\s+emoji-id=(["'])([^"']+)\1\s*>([\s\S]*?)<\/tg-emoji>/gi;

const decodeHtmlText = (value: string) => {
  if (typeof document === "undefined") {
    return value
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&amp;/g, "&");
  }
  const element = document.createElement("textarea");
  element.innerHTML = value;
  return element.value;
};

const escapeHtmlText = (value: string) => value
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const mergeTextNodes = (nodes: readonly EmojiTextNode[]): EmojiTextNode[] => {
  const result: EmojiTextNode[] = [];
  for (const node of nodes) {
    if (node.type === "text") {
      if (!node.value) continue;
      const previous = result.at(-1);
      if (previous?.type === "text") previous.value += node.value;
      else result.push({ type: "text", value: node.value });
    } else if (node.customEmojiId.trim() && node.fallback) {
      result.push({
        type: "custom_emoji",
        customEmojiId: node.customEmojiId.trim(),
        fallback: node.fallback
      });
    }
  }
  return result;
};

type GraphemeSegment = {
  value: string;
  start: number;
  end: number;
  safe: boolean;
};

type SegmenterLike = {
  segment: (value: string) => Iterable<{ segment: string; index: number }>;
};

type SegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity: "grapheme" }
) => SegmenterLike;

const emojiGraphemePattern = /(?:\p{Extended_Pictographic}|\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3)/u;
const unsafeFallbackPartPattern = /(?:\u200D|\uFE0F|\u20E3|\p{Regional_Indicator}|\p{Emoji_Modifier})/u;

const segmentGraphemes = (value: string): GraphemeSegment[] => {
  const Segmenter = (Intl as unknown as { Segmenter?: SegmenterConstructor }).Segmenter;
  if (Segmenter) {
    return Array.from(new Segmenter("ru", { granularity: "grapheme" }).segment(value), (item) => ({
      value: item.segment,
      start: item.index,
      end: item.index + item.segment.length,
      safe: true
    }));
  }

  const codePoints = Array.from(value);
  const segments: GraphemeSegment[] = [];
  let offset = 0;
  for (let index = 0; index < codePoints.length; index += 1) {
    const part = codePoints[index];
    const previous = codePoints[index - 1] ?? "";
    const next = codePoints[index + 1] ?? "";
    segments.push({
      value: part,
      start: offset,
      end: offset + part.length,
      safe: !unsafeFallbackPartPattern.test(part) &&
        !unsafeFallbackPartPattern.test(previous) &&
        !unsafeFallbackPartPattern.test(next)
    });
    offset += part.length;
  }
  return segments;
};

const isEmojiGrapheme = (segment: GraphemeSegment) =>
  segment.safe && emojiGraphemePattern.test(segment.value);

export const resolveEmojiReplacementTarget = (
  nodes: readonly EmojiTextNode[],
  selectionStart: number,
  selectionEnd: number
): EmojiReplacementTarget | null => {
  if (!Number.isSafeInteger(selectionStart) || !Number.isSafeInteger(selectionEnd)) {
    return null;
  }
  const start = Math.max(0, Math.min(selectionStart, selectionEnd));
  const end = Math.max(0, Math.max(selectionStart, selectionEnd));
  const candidates: Array<GraphemeSegment & { kind: EmojiTextNode["type"] }> = [];
  let cursor = 0;

  for (const node of nodes) {
    if (node.type === "custom_emoji") {
      candidates.push({
        kind: node.type,
        value: node.fallback,
        start: cursor,
        end: cursor + node.fallback.length,
        safe: false
      });
      cursor += node.fallback.length;
      continue;
    }
    for (const segment of segmentGraphemes(node.value)) {
      candidates.push({
        ...segment,
        kind: node.type,
        start: cursor + segment.start,
        end: cursor + segment.end
      });
    }
    cursor += node.value.length;
  }

  const candidate = start === end
    ? [...candidates].reverse().find((item) => item.end === start)
    : candidates.find((item) => item.start === start && item.end === end);
  if (!candidate || candidate.kind !== "text" || !isEmojiGrapheme(candidate)) {
    return null;
  }
  return {
    start: candidate.start,
    end: candidate.end,
    fallback: candidate.value
  };
};

export const replaceEmojiTarget = (
  nodes: readonly EmojiTextNode[],
  target: EmojiReplacementTarget,
  token: CustomEmojiToken
): EmojiTextNode[] => {
  if (
    !Number.isSafeInteger(target.start) ||
    !Number.isSafeInteger(target.end) ||
    target.start < 0 ||
    target.end <= target.start ||
    target.end - target.start !== target.fallback.length
  ) {
    return mergeTextNodes(nodes);
  }

  const next: EmojiTextNode[] = [];
  let cursor = 0;
  let replaced = false;
  for (const node of nodes) {
    const length = node.type === "text" ? node.value.length : node.fallback.length;
    const nodeEnd = cursor + length;
    if (
      !replaced &&
      node.type === "text" &&
      target.start >= cursor &&
      target.end <= nodeEnd
    ) {
      const localStart = target.start - cursor;
      const localEnd = target.end - cursor;
      if (node.value.slice(localStart, localEnd) === target.fallback) {
        next.push(
          { type: "text", value: node.value.slice(0, localStart) },
          token,
          { type: "text", value: node.value.slice(localEnd) }
        );
        replaced = true;
      } else {
        next.push(node);
      }
    } else {
      next.push(node);
    }
    cursor = nodeEnd;
  }
  return mergeTextNodes(replaced ? next : nodes);
};

export const parseTelegramEmojiHtml = (html: string): EmojiTextNode[] => {
  const nodes: EmojiTextNode[] = [];
  let offset = 0;
  customEmojiPattern.lastIndex = 0;
  for (let match = customEmojiPattern.exec(html); match; match = customEmojiPattern.exec(html)) {
    if (match.index > offset) {
      nodes.push({ type: "text", value: decodeHtmlText(html.slice(offset, match.index)) });
    }
    const customEmojiId = match[2].trim();
    const fallback = decodeHtmlText(match[3]).trim();
    if (customEmojiId && fallback && !/[<>]/.test(match[3])) {
      nodes.push({ type: "custom_emoji", customEmojiId, fallback });
    } else {
      nodes.push({ type: "text", value: match[0] });
    }
    offset = match.index + match[0].length;
  }
  if (offset < html.length) {
    nodes.push({ type: "text", value: decodeHtmlText(html.slice(offset)) });
  }
  return mergeTextNodes(nodes);
};

export const serializeTelegramEmojiHtml = (nodes: readonly EmojiTextNode[]) =>
  mergeTextNodes(nodes).map((node) => node.type === "text"
    ? escapeHtmlText(node.value)
    : `<tg-emoji emoji-id="${escapeHtmlText(node.customEmojiId)}">${escapeHtmlText(node.fallback)}</tg-emoji>`
  ).join("");

export const insertCustomEmojiAt = (
  nodes: readonly EmojiTextNode[],
  renderedOffset: number,
  token: CustomEmojiToken
): EmojiTextNode[] => {
  const safeOffset = Math.max(0, Math.floor(renderedOffset));
  const next: EmojiTextNode[] = [];
  let cursor = 0;
  let inserted = false;
  for (const node of nodes) {
    const length = node.type === "text" ? node.value.length : 1;
    if (!inserted && safeOffset <= cursor + length) {
      if (node.type === "text") {
        const local = Math.max(0, Math.min(node.value.length, safeOffset - cursor));
        if (local > 0) next.push({ type: "text", value: node.value.slice(0, local) });
        next.push(token);
        if (local < node.value.length) next.push({ type: "text", value: node.value.slice(local) });
      } else if (safeOffset <= cursor) {
        next.push(token, node);
      } else {
        next.push(node, token);
      }
      inserted = true;
    } else {
      next.push(node);
    }
    cursor += length;
  }
  if (!inserted) next.push(token);
  return mergeTextNodes(next);
};

export const removeNodeAt = (
  nodes: readonly EmojiTextNode[],
  nodeIndex: number
): EmojiTextNode[] => mergeTextNodes(nodes.filter((_, index) => index !== nodeIndex));

export const plainEmojiText = (nodes: readonly EmojiTextNode[]) =>
  nodes.map((node) => node.type === "text" ? node.value : node.fallback).join("");
